/**
 * FlowHead — der Kopf eines Knotens bzw. einer Zeile, in drei Ausprägungen:
 * `<a>` (Link), `<button>` (Aktion) oder `<span>` (nur Text).
 *
 * Warum ein Link und kein Button-mit-navigate: eine Status-Webapp lebt davon,
 * mehrere Läufe nebeneinander zu öffnen. Nur ein echtes `<a href>` kann
 * Mittelklick, Cmd/Strg-Klick und „Link-Adresse kopieren". `Link` aus
 * react-router (bereits Dependency) rendert genau das und fängt allein den
 * schlichten Linksklick für die clientseitige Navigation ab.
 *
 * Die Markup-Regel gilt in beiden Varianten unverändert: der Kopf ist das
 * EINZIGE interaktive Element: sekundäre Aktionen, Kette und Trail sind
 * Geschwister daneben. Ein `<button>` in einem `<a>` ist genauso ungültig wie
 * ein `<button>` in einem `<button>`.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export interface FlowHeadProps {
  /**
   * Ziel des Kopfs. Gesetzt ⇒ `<a>`. Absolute URLs (`http(s)://`, `//`) werden
   * als externes `<a>` gerendert, alles andere als react-router-`Link`.
   */
  href?: string;
  /** Ohne `href`: macht den Kopf zum `<button>`. Mit `href`: zusätzlicher Nebeneffekt. */
  onActivate?: () => void;
  className?: string;
  /** Zusatzklasse, sobald der Kopf interaktiv ist (Link ODER Button). */
  interactiveClassName?: string;
  "aria-label"?: string;
  "aria-current"?: "true";
  "data-flow-node"?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Klick soll die umgebende Zeile nicht mitauslösen. */
  stopPropagation?: boolean;
  children: ReactNode;
}

const ABSOLUTE = /^([a-z][a-z0-9+.-]*:|\/\/)/i;

export function FlowHead({
  href,
  onActivate,
  className,
  interactiveClassName,
  stopPropagation,
  children,
  ...rest
}: FlowHeadProps) {
  const interactive = href != null || onActivate != null;
  const cls = [className, interactive && interactiveClassName].filter(Boolean).join(" ") || undefined;

  if (href != null) {
    const onClick = (e: { stopPropagation: () => void }) => {
      if (stopPropagation) e.stopPropagation();
      onActivate?.();
    };
    // Kein preventDefault: Navigation, Mittelklick und Modifier-Klick bleiben
    // dem Browser bzw. react-router überlassen.
    return ABSOLUTE.test(href) ? (
      <a {...rest} href={href} className={cls} onClick={onClick}>
        {children}
      </a>
    ) : (
      <Link {...rest} to={href} className={cls} onClick={onClick}>
        {children}
      </Link>
    );
  }

  if (onActivate) {
    return (
      <button
        {...rest}
        type="button"
        className={cls}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          onActivate();
        }}
      >
        {children}
      </button>
    );
  }

  return (
    <span {...rest} className={cls}>
      {children}
    </span>
  );
}
